import { 
    Box, 
    Typography, 
    Button, 
    Card,
    CardContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tabs,
    Tab,
    LinearProgress
} from "@mui/material";
import { useState } from "react";
import { useTheme } from "@mui/material";
import moment from "moment";
import GetAppIcon from '@mui/icons-material/GetApp';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { useGetProductivityAttendanceAnalyticsQuery } from "../../state/api";

const ProductivityAnalytics = () => {
    const theme = useTheme();
    
    const [selectedPeriod, setSelectedPeriod] = useState({
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD')
    });
    
    const [selectedDepartment, setSelectedDepartment] = useState('all');
    const [activeTab, setActiveTab] = useState(0);
    
    const { data: analyticsData, isLoading, isError, refetch } = useGetProductivityAttendanceAnalyticsQuery({
        ...selectedPeriod,
        department: selectedDepartment
    });

    const handlePeriodChange = (field, value) => {
        setSelectedPeriod(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleExportData = () => {
        if (!analyticsData?.employeeAnalytics) return;
        
        const headers = ['Employee Name', 'Department', 'Total Hours', 'Days Worked', 'Days Scheduled', 'Avg Hours/Day', 'Attendance Rate', 'Punctuality Rate'];
        const csvContent = [
            headers.join(','),
            ...analyticsData.employeeAnalytics.map(emp => [
                emp.employeeName,
                emp.department || 'N/A',
                emp.totalHours,
                emp.daysWorked,
                emp.daysScheduled,
                emp.averageHoursPerDay,
                emp.attendanceRate,
                emp.punctualityRate
            ].join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `productivity-analytics-${selectedPeriod.startDate}-to-${selectedPeriod.endDate}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const getAttendanceColor = (rate) => {
        if (rate >= 95) return 'success';
        if (rate >= 85) return 'warning';
        return 'error';
    };

    const getPunctualityColor = (rate) => {
        if (rate >= 90) return 'success';
        if (rate >= 75) return 'warning';
        return 'error';
    };

    const departments = analyticsData?.departmentAnalytics?.map(dept => dept.department) || [];

    if (isLoading) {
        return <Typography>Loading productivity & attendance analytics...</Typography>;
    }

    if (isError) {
        return <Typography color="error">Error loading analytics data.</Typography>;
    }

    return (
        <Box m="1.5rem 2.5rem">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb="2rem">
                <Typography variant="h4" color={theme.palette.secondary[100]}>
                    Productivity & Attendance Analytics
                </Typography>
                <Box display="flex" gap="1rem" alignItems="center">
                    <TextField
                        label="Start Date"
                        type="date"
                        value={selectedPeriod.startDate}
                        onChange={(e) => handlePeriodChange('startDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        value={selectedPeriod.endDate}
                        onChange={(e) => handlePeriodChange('endDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                    />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Department</InputLabel>
                        <Select
                            value={selectedDepartment}
                            onChange={(e) => setSelectedDepartment(e.target.value)}
                            label="Department"
                        >
                            <MenuItem value="all">All Departments</MenuItem>
                            {departments.map(dept => (
                                <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="outlined"
                        onClick={() => refetch()}
                        sx={{ color: theme.palette.secondary[100], borderColor: theme.palette.secondary[100] }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<GetAppIcon />}
                        onClick={handleExportData}
                        sx={{
                            backgroundColor: theme.palette.secondary.light,
                            color: theme.palette.background.alt,
                        }}
                    >
                        Export CSV
                    </Button>
                </Box>
            </Box>

            {/* Overall Metrics Cards */}
            <Grid container spacing={3} mb="2rem">
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap="1rem">
                                <GroupIcon color="primary" />
                                <Box>
                                    <Typography variant="h6" color="primary">
                                        {analyticsData?.overallMetrics?.totalEmployees || 0}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Active Employees
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap="1rem">
                                <AccessTimeIcon color="secondary" />
                                <Box>
                                    <Typography variant="h6" color="secondary">
                                        {analyticsData?.overallMetrics?.totalHours || 0}h
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Total Hours Worked
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap="1rem">
                                <CheckCircleIcon color="success" />
                                <Box>
                                    <Typography variant="h6" color="success.main">
                                        {analyticsData?.overallMetrics?.averageAttendanceRate || 0}%
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Avg Attendance Rate
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap="1rem">
                                <TrendingUpIcon color="info" />
                                <Box>
                                    <Typography variant="h6" color="info.main">
                                        {analyticsData?.overallMetrics?.averagePunctualityRate || 0}%
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Avg Punctuality Rate
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Performance Metrics Cards */}
            <Grid container spacing={3} mb="2rem">
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Average Hours per Employee</Typography>
                            <Typography variant="h4" color="primary">
                                {analyticsData?.overallMetrics?.averageHoursPerEmployee || 0}h
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Per employee in selected period
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Attendance Performance</Typography>
                            <Box display="flex" alignItems="center" gap="1rem">
                                <LinearProgress 
                                    variant="determinate" 
                                    value={analyticsData?.overallMetrics?.averageAttendanceRate || 0} 
                                    sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                                    color={getAttendanceColor(analyticsData?.overallMetrics?.averageAttendanceRate || 0)}
                                />
                                <Typography variant="body2" fontWeight="bold">
                                    {analyticsData?.overallMetrics?.averageAttendanceRate || 0}%
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="textSecondary" mt={1}>
                                Company-wide attendance rate
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Punctuality Performance</Typography>
                            <Box display="flex" alignItems="center" gap="1rem">
                                <LinearProgress 
                                    variant="determinate" 
                                    value={analyticsData?.overallMetrics?.averagePunctualityRate || 0} 
                                    sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                                    color={getPunctualityColor(analyticsData?.overallMetrics?.averagePunctualityRate || 0)}
                                />
                                <Typography variant="body2" fontWeight="bold">
                                    {analyticsData?.overallMetrics?.averagePunctualityRate || 0}%
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="textSecondary" mt={1}>
                                Company-wide punctuality rate
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Analytics Tables */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                    <Tab label="Employee Analytics" />
                    <Tab label="Department Analytics" />
                </Tabs>

                {activeTab === 0 && (
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Employee</TableCell>
                                    <TableCell>Department</TableCell>
                                    <TableCell align="right">Total Hours</TableCell>
                                    <TableCell align="right">Days Worked</TableCell>
                                    <TableCell align="right">Days Scheduled</TableCell>
                                    <TableCell align="right">Avg Hours/Day</TableCell>
                                    <TableCell align="center">Attendance Rate</TableCell>
                                    <TableCell align="center">Punctuality Rate</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {analyticsData?.employeeAnalytics?.map((employee) => (
                                    <TableRow key={employee.employeeId} hover>
                                        <TableCell>
                                            <Typography variant="body1" fontWeight="bold">
                                                {employee.employeeName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={employee.department || 'N/A'} 
                                                size="small" 
                                                color="primary" 
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="bold" color="primary">
                                                {employee.totalHours}h
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                {employee.daysWorked}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="textSecondary">
                                                {employee.daysScheduled}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                {employee.averageHoursPerDay}h
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box display="flex" alignItems="center" gap="0.5rem">
                                                <LinearProgress 
                                                    variant="determinate" 
                                                    value={employee.attendanceRate} 
                                                    sx={{ width: 60, height: 6, borderRadius: 3 }}
                                                    color={getAttendanceColor(employee.attendanceRate)}
                                                />
                                                <Typography variant="body2" fontWeight="bold" color={`${getAttendanceColor(employee.attendanceRate)}.main`}>
                                                    {employee.attendanceRate}%
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box display="flex" alignItems="center" gap="0.5rem">
                                                <LinearProgress 
                                                    variant="determinate" 
                                                    value={employee.punctualityRate} 
                                                    sx={{ width: 60, height: 6, borderRadius: 3 }}
                                                    color={getPunctualityColor(employee.punctualityRate)}
                                                />
                                                <Typography variant="body2" fontWeight="bold" color={`${getPunctualityColor(employee.punctualityRate)}.main`}>
                                                    {employee.punctualityRate}%
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {activeTab === 1 && (
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Department</TableCell>
                                    <TableCell align="right">Employees</TableCell>
                                    <TableCell align="right">Total Hours</TableCell>
                                    <TableCell align="right">Avg Hours/Employee</TableCell>
                                    <TableCell align="center">Attendance Rate</TableCell>
                                    <TableCell align="center">Punctuality Rate</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {analyticsData?.departmentAnalytics?.map((department) => (
                                    <TableRow key={department.department} hover>
                                        <TableCell>
                                            <Typography variant="body1" fontWeight="bold">
                                                {department.department}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Chip 
                                                label={department.totalEmployees} 
                                                size="small" 
                                                color="primary"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="bold" color="primary">
                                                {department.totalHours}h
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                {department.averageHoursPerEmployee}h
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box display="flex" alignItems="center" gap="0.5rem">
                                                <LinearProgress 
                                                    variant="determinate" 
                                                    value={department.attendanceRate} 
                                                    sx={{ width: 80, height: 8, borderRadius: 4 }}
                                                    color={getAttendanceColor(department.attendanceRate)}
                                                />
                                                <Typography variant="body2" fontWeight="bold" color={`${getAttendanceColor(department.attendanceRate)}.main`}>
                                                    {department.attendanceRate}%
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box display="flex" alignItems="center" gap="0.5rem">
                                                <LinearProgress 
                                                    variant="determinate" 
                                                    value={department.punctualityRate} 
                                                    sx={{ width: 80, height: 8, borderRadius: 4 }}
                                                    color={getPunctualityColor(department.punctualityRate)}
                                                />
                                                <Typography variant="body2" fontWeight="bold" color={`${getPunctualityColor(department.punctualityRate)}.main`}>
                                                    {department.punctualityRate}%
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
};

export default ProductivityAnalytics; 